import Constants from 'expo-constants';
import { useRouter } from 'expo-router';
import React, { useEffect } from 'react';
import { Alert, Linking, Platform, ScrollView, View } from 'react-native';
import { TapGestureHandler } from 'react-native-gesture-handler';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import { useModalStore } from '@/components/login-modal/store';
import { isDesktop, useAuth } from '@/core';
import { clearAll } from '@/core/storage';
import useSettingsStore from '@/stores/use-settings-store';
import { Button, Card, Text } from '@/ui';
import { ConfirmModal } from '@/ui/confirm-modal';
import { Icon } from '@/ui/icon';
import {
  ArrowRight,
  Car,
  Docs,
  Exit,
  Gift,
  Info,
  Person,
  Receipts,
  Wallet,
} from '@/ui/icons';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { Loader } from '@/ui/loader';
import { MenuItem } from '@/ui/menu-item';

export default function Account() {
  const { styles, theme } = useStyles(stylesheet);
  const status = useAuth.use.status();
  const loginMode = useModalStore((state) => state.loginModalMode);
  const setLoginModalMode = useModalStore((state) => state.setLoginModalMode);
  const setPartnerMode = useSettingsStore((state) => state.setPartnerMode);
  const router = useRouter();

  const [clearCacheModalVisible, setClearCacheModalVisible] =
    React.useState(false);

  const handleLogout = () => {
    setPartnerMode(false);
    signOut();
    router.replace('/search');

    Toast.show({
      text1: 'You have logged out',
      type: 'success',
    });
  };

  const {
    data: customer,
    isLoading: isGettingCustomer,
    isError: isGettingCustomerError,
    refetch: refetchCustomer,
  } = useCustomer({
    retry: false,
  });

  useEffect(() => {
    if (status === 'signIn') {
      const fetchCustomer = async () => {
        await refetchCustomer();
      };

      fetchCustomer();
    }
  }, [status, customer]);

  const signOut = useAuth.use.signOut();

  const clearCache = () => {
    if (Platform.OS === 'web') {
      setClearCacheModalVisible(true);
      return;
    }

    Alert.alert('Clear cache?', 'Do it.', [
      {
        text: 'Cancel',
      },
      {
        text: 'Delete',
        onPress: () => {
          clearAll();
        },
        style: 'destructive',
      },
    ]);
  };

  const handleOpenReferral = () => {
    router.push('/account/referrals');
  };

  const handleSwitchToPartner = async () => {
    if (customer?.partnerToken) {
      setPartnerMode(true);
      router.replace('/partner/dashboard');
      return;
    }

    router.push('/join-partner-program');
  };

  if (isGettingCustomer) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollContainer}
        style={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={false}
      >
        <View>
          <Text type="title1">
            {customer?.firstname
              ? `${customer?.firstname} ${customer?.lastname}`
              : ''}{' '}
          </Text>

          <Text
            type="bodyRegular"
            style={{ textDecorationLine: 'none', userSelect: 'none' }}
            selectable={false}
          >
            {customer?.email}
          </Text>
        </View>

        <Card>
          <View style={{ gap: 8, padding: 16 }}>
            <Text type="bodyRegular" tx="account.eonCredits" />

            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Text type="largeTitle">
                {`$${(customer?.credits || 0) / 100}`}
              </Text>

              <Button
                tx="account.moreCredit"
                type="primaryExtraSmall"
                rightIcon={
                  <Icon
                    type="naked"
                    icon={<ArrowRight />}
                    fillColor={theme.colors.button.primary.default.text}
                    width={10}
                    height={10}
                  />
                }
                onPress={handleOpenReferral}
              />
            </View>
          </View>
        </Card>

        <Card>
          <MenuItem
            leftIcon={<Person />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            tx="account.profile"
            onPress={() => {
              router.push('/account/profile');
            }}
            style={{ width: '100%' }}
          />

          <MenuItem
            leftIcon={<Wallet />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            tx="account.paymentMethods"
            onPress={() => {
              router.push('/account/payment-methods');
            }}
            style={{ width: '100%' }}
          />

          <MenuItem
            leftIcon={<Receipts />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            tx="account.receipts"
            onPress={() => {
              router.push('/account/trip-history');
            }}
            style={{ width: '100%' }}
          />

          <MenuItem
            leftIcon={<Gift />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            tx="account.referrals"
            onPress={() => {
              router.push('/account/referrals');
            }}
            style={{ width: '100%' }}
          />

          <MenuItem
            leftIcon={<Car />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            tx="account.becomeAPartner"
            onPress={async () => {
              if (Platform.OS === 'web') {
                window.open('https://eonrides.com/partners', '_blank');
              } else {
                Linking.openURL('https://eonrides.com/partners');
              }
            }}
            style={{ width: '100%' }}
          />
        </Card>

        <Card>
          <MenuItem
            leftIcon={<Info />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            style={{ width: '100%' }}
            tx="account.supportFaq"
            onPress={() => {
              router.push('/account/support-faq');
            }}
          />
        </Card>

        <Card>
          <MenuItem
            leftIcon={<Docs />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            style={{ width: '100%' }}
            tx="account.legal"
            onPress={() => {
              router.push('/account/legal');
            }}
          />

          <MenuItem
            leftIcon={<Exit />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            style={{ width: '100%' }}
            tx="account.logout"
            onPress={handleLogout}
          />
        </Card>

        <View style={{ alignItems: 'center', marginTop: 16 }}>
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            Version {Constants.expoConfig?.version}
          </Text>
        </View>

        {!isDesktop() && customer?.partnerToken ? (
          <View style={{ alignItems: 'center' }}>
            <Button
              text="Switch to partner"
              type="primaryExtraSmall"
              onPress={handleSwitchToPartner}
              style={{ width: 130 }}
            />
          </View>
        ) : null}

        <TapGestureHandler onActivated={clearCache} numberOfTaps={3}>
          <View style={{ width: 100, height: 100 }} />
        </TapGestureHandler>

        <ConfirmModal
          isVisible={clearCacheModalVisible}
          titleTx="account.clearCache"
          bodyTx="account.clearCache"
          leftButton={{
            tx: 'account.cancel',
            onPress: () => {
              setClearCacheModalVisible(false);
            },
          }}
          rightButton={{
            tx: 'account.clearCache',
            onPress: () => {
              clearAll();
              setClearCacheModalVisible(false);
            },
          }}
          onDismiss={() => {
            setClearCacheModalVisible(false);
          }}
        />
      </ScrollView>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    backgroundColor: theme.colors.background_1,
    flex: 1,
    alignItems: {
      sm: 'center',
    },
  },
  scrollContainer: {
    flexGrow: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    gap: 16,
    backgroundColor: theme.colors.background_1,
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 600,
    },
    width: {
      xs: '100%',
      sm: 600,
      md: 600,
    },
  },
}));
